import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { CalendarIcon, DownloadIcon } from '@heroicons/react/outline'
import { DateRangePicker } from 'react-date-range'
import Layout from 'containers/Layout'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import apiGetBlob from 'lib/network/apiGetBlob'
import notify from 'components/atoms/Notify'
import Table from 'components/pageComponents/Table'
import EmptyState from 'components/pageComponents/EmptyState'
import LoadingIcon from 'components/atoms/LoadingIcon'
import Button from 'components/atoms/Button'
import Popover from 'components/molecules/Popover'
import PageFooter from 'components/paginationComponent'
import Selection from 'components/molecules/Selection'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

const namesOrAll = (arr, t) => {
  if (!arr || arr.length === 0) return t('officeEvaluationReports.all')
  return arr
    .map(x => x?.name)
    .filter(Boolean)
    .join(', ')
}

const OfficeEvaluationReports = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const popover = useRef()

  const [companies, setCompanies] = useState([])
  const [branches, setBranches] = useState([])
  const [companyIds, setCompanyIds] = useState([])
  const [branchIds, setBranchIds] = useState([])
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })
  const [bookDateTime, setBookDateTime] = useState(false)
  const [bookStartDateTime, setBookStartDateTime] = useState(false)
  const [bookEndDateTime, setBookEndDateTime] = useState(false)
  const [generating, setGenerating] = useState(false)

  const [reports, setReports] = useState([])
  const [pagination, setPagination] = useState({})
  const [loading, setLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState(null)

  const fetchCompanies = async () => {
    try {
      const companyRes = await apiGet('companies', { pageSize: 1000 })
      const companyList = companyRes?.data?.company || companyRes?.data?.companies || []
      setCompanies(companyList.map(c => ({ id: c._id, name: c.name || c.fullName })))
    } catch (error) {
      notify.error(t('officeEvaluationReports.messages.loadFiltersFailed'))
    }
  }

  const fetchBranchesForCompanies = async ids => {
    if (!ids.length) {
      setBranches([])
      setBranchIds([])
      return
    }
    try {
      const results = await Promise.all(ids.map(id => apiGet(`company/${id}/branches`)))
      const seen = new Set()
      const merged = []
      for (const res of results) {
        for (const b of res?.data || []) {
          if (b?._id && !seen.has(String(b._id))) {
            seen.add(String(b._id))
            merged.push({ id: b._id, name: b.name })
          }
        }
      }
      setBranches(merged)
      setBranchIds(prev => prev.filter(bid => seen.has(String(bid))))
    } catch (error) {
      notify.error(t('officeEvaluationReports.messages.loadFiltersFailed'))
      setBranches([])
    }
  }

  const fetchReports = async (page = 1) => {
    if (page === 1) setLoading(true)
    try {
      const response = await apiGet('office-evaluation-reports', { page, size: 20 })
      const list = response?.data?.list || []
      setReports(prev => (page === 1 ? list : [...prev, ...list]))
      setPagination(response?.data?.pagination || {})
    } catch (error) {
      notify.error(t('officeEvaluationReports.messages.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCompanies()
    fetchReports(1)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    fetchBranchesForCompanies(companyIds)
    // eslint-disable-next-line
  }, [companyIds])

  const handleSelect = range => {
    setDateRange({
      startDate: moment(range.selection.startDate),
      endDate: moment(range.selection.endDate),
    })
  }

  const handleGenerate = async () => {
    try {
      setGenerating(true)
      const payload = {
        companyIds,
        branchIds,
        sd: dateRange.startDate ? `${dateRange.startDate.format('YYYY-MM-DD')} 00:00` : undefined,
        ed: dateRange.endDate ? `${dateRange.endDate.format('YYYY-MM-DD')} 23:59` : undefined,
        bookDateTime,
        bookStartDateTime,
        bookEndDateTime,
      }
      const response = await apiPost('office-evaluation-reports', payload)
      if (response?.success) {
        notify.success(t('officeEvaluationReports.messages.generated'))
        fetchReports(1)
        // Auto-download the report that was just generated.
        if (response?.data?._id) handleDownload(response.data)
        // Reset the date range + its checkboxes after creating the report.
        setDateRange({ startDate: null, endDate: null })
        setBookDateTime(false)
        setBookStartDateTime(false)
        setBookEndDateTime(false)
      }
    } catch (error) {
      notify.error(error?.message || t('officeEvaluationReports.messages.generateFailed'))
    } finally {
      setGenerating(false)
    }
  }

  const handleDownload = async report => {
    const reportId = report?._id
    if (!reportId) return
    setDownloadingId(reportId)
    try {
      const blob = await apiGetBlob(`office-evaluation-reports/${reportId}/download`)
      if (!blob) {
        notify.error(t('officeEvaluationReports.messages.downloadFailed'))
        return
      }
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = report?.fileName || `office-evaluations-${moment(report?.createdAt).format('YYYY-MM-DD')}.xlsx`
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      notify.error(t('officeEvaluationReports.messages.downloadFailed'))
    } finally {
      setDownloadingId(null)
    }
  }

  const columns = [
    {
      width: '13%',
      name: t('officeEvaluationReports.columns.generatedBy'),
      key: 'generatedBy',
      rendered: item => <span className="text-[13px]">{item?.generatedBy?.fullName || '—'}</span>,
    },
    {
      width: '16%',
      name: t('officeEvaluationReports.columns.company'),
      key: 'company',
      rendered: item => <span className="text-[13px]">{namesOrAll(item?.companies, t)}</span>,
    },
    {
      width: '16%',
      name: t('officeEvaluationReports.columns.branch'),
      key: 'branch',
      rendered: item => <span className="text-[13px]">{namesOrAll(item?.branches, t)}</span>,
    },
    {
      width: '15%',
      name: t('officeEvaluationReports.columns.dateRange'),
      key: 'dateRange',
      rendered: item =>
        item?.startDate || item?.endDate ? (
          <span className="text-[13px]">
            {item?.startDate ? moment(item.startDate).format('DD MMM YYYY') : '…'} - {item?.endDate ? moment(item.endDate).format('DD MMM YYYY') : '…'}
          </span>
        ) : (
          <span className="text-[13px] text-gray-400">{t('officeEvaluationReports.all')}</span>
        ),
    },
    {
      width: '7%',
      name: t('officeEvaluationReports.columns.records'),
      key: 'records',
      rendered: item => <span className="text-[13px] font-semibold">{item?.recordCount ?? 0}</span>,
    },
    {
      width: '14%',
      name: t('officeEvaluationReports.columns.createdAt'),
      key: 'createdAt',
      rendered: item => <span className="text-[13px]">{item?.createdAt ? moment(item.createdAt).format('DD MMM YYYY, HH:mm') : '—'}</span>,
    },
    {
      width: '11%',
      name: t('officeEvaluationReports.columns.action'),
      key: 'action',
      rendered: item => (
        <button
          type="button"
          onClick={() => handleDownload(item)}
          disabled={downloadingId === item?._id}
          className="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-dark-600 px-3 py-1.5 text-[13px] font-medium text-white shadow-sm transition hover:bg-dark-700 disabled:opacity-50"
        >
          <DownloadIcon className="h-4 w-4" />
          {downloadingId === item?._id ? t('officeEvaluationReports.buttons.downloading') : t('officeEvaluationReports.buttons.download')}
        </button>
      ),
    },
  ]

  return (
    <Layout
      headerRight={
        <div className="flex items-center justify-end gap-4">
          <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
        </div>
      }
      title={t('officeEvaluationReports.title')}
    >
      <div className="space-y-6 pt-8">
        <div className="ml-2 grid grid-cols-1 gap-4 md:ml-4 md:grid-cols-3 md:gap-6">
          <div>
            <label className="mb-1 block text-sm font-normal text-gray-700">{t('officeEvaluationReports.filters.company')}</label>
            <Selection
              multiple
              title={t('officeEvaluationReports.filters.selectCompany')}
              list={companies}
              value={companyIds}
              onChange={ids => setCompanyIds(ids || [])}
              className="w-full"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-normal text-gray-700">{t('officeEvaluationReports.filters.branch')}</label>
            <Selection
              multiple
              disabled={!companyIds.length}
              title={companyIds.length ? t('officeEvaluationReports.filters.selectBranch') : t('officeEvaluationReports.filters.selectCompanyFirst')}
              list={branches}
              value={branchIds}
              onChange={ids => setBranchIds(ids || [])}
              className="w-full"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t('officeEvaluationReports.filters.dateRange')}</label>
            <Popover
              custom
              placement="bottom-start"
              ref={popover}
              content={close => (
                <DateRangePicker
                  weekStartsOn={0}
                  startDatePlaceholder={t('reports.datePicker.early')}
                  endDatePlaceholder={t('reports.datePicker.continuous')}
                  footerContent={
                    <>
                      <button
                        onClick={close}
                        className="mt-4 mr-2 w-[100px] rounded-md border border-gray-300 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm outline-none hover:bg-gray-50"
                      >
                        {t('reports.datePicker.close')}
                      </button>
                      <button
                        disabled={!dateRange.startDate && !dateRange.endDate}
                        onClick={() => setDateRange({ startDate: null, endDate: null })}
                        className="mt-4 mr-2 w-[100px] rounded-md border border-gray-300 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm outline-none hover:bg-gray-50 disabled:opacity-50"
                      >
                        {t('reports.datePicker.clear')}
                      </button>
                      <div className="mt-4 flex flex-col items-start gap-2">
                        <label className="flex w-auto items-center space-x-2 rounded px-2 py-1">
                          <input type="checkbox" checked={bookDateTime} onChange={e => setBookDateTime(e.target.checked)} />
                          <span>{t('officeEvaluationReports.filters.bookDateTime')}</span>
                        </label>
                        <label className="flex w-auto items-center space-x-2 rounded px-2 py-1">
                          <input type="checkbox" checked={bookStartDateTime} onChange={e => setBookStartDateTime(e.target.checked)} />
                          <span>{t('officeEvaluationReports.filters.bookStartDateTime')}</span>
                        </label>
                        <label className="flex w-auto items-center space-x-2 rounded px-2 py-1">
                          <input type="checkbox" checked={bookEndDateTime} onChange={e => setBookEndDateTime(e.target.checked)} />
                          <span>{t('officeEvaluationReports.filters.bookEndDateTime')}</span>
                        </label>
                      </div>
                    </>
                  }
                  ranges={[
                    {
                      startDate: dateRange.startDate ? dateRange.startDate.toDate() : new Date(),
                      endDate: dateRange.endDate ? dateRange.endDate.toDate() : new Date(),
                      key: 'selection',
                    },
                  ]}
                  onChange={handleSelect}
                />
              )}
            >
              <button
                type="button"
                className="font-regular inline-flex h-[40px] w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 text-sm leading-4 text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <CalendarIcon className={`h-4 w-4 flex-shrink-0 text-gray-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  <span className="truncate text-left">
                    {dateRange.startDate && dateRange.endDate
                      ? `${dateRange.startDate.format('MMM Do, YYYY')} - ${dateRange.endDate.format('MMM Do, YYYY')}`
                      : t('officeEvaluationReports.filters.selectDateRange')}
                  </span>
                </div>
                <svg className={`h-4 w-4 flex-shrink-0 text-gray-400 ${isRTL ? 'mr-2' : 'ml-2'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </Popover>
          </div>
        </div>

        <div className="ml-2 mt-3 md:ml-4">
          <Button onClick={handleGenerate} disabled={generating} className="w-full sm:w-auto">
            {generating ? t('officeEvaluationReports.buttons.generating') : t('officeEvaluationReports.buttons.generate')}
          </Button>
        </div>

        <div className="flex flex-1 items-stretch">
          <main className="flex-1">
            <div className="inline-block min-w-full align-middle">
              <div className="min-w-full overflow-x-auto">
                {loading ? (
                  <div className="flex h-[200px] w-full items-center justify-center border-0">
                    <LoadingIcon />
                  </div>
                ) : reports.length === 0 ? (
                  <EmptyState v2 description={t('officeEvaluationReports.messages.noReports')} />
                ) : (
                  <div className="min-w-[900px]">
                    <Table
                      hasMore={pagination?.currentPage !== pagination?.totalPages}
                      fetchData={() => fetchReports((pagination?.currentPage || 1) + 1)}
                      data={reports}
                      columns={columns}
                    />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default OfficeEvaluationReports
